import React from 'react';
import { Target, Clock, PoundSterling, Activity } from 'lucide-react';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import type { UtilisationSummary } from '@/types/utilisation';

interface KeyPerformanceIndicatorsProps {
  summary: UtilisationSummary;
  onCardClick?: (cardType: 'utilisation' | 'hours' | 'billable' | 'peak') => void;
}

const getUtilisationVariant = (utilisation: number): 'success' | 'warning' | 'error' | 'info' => {
  if (utilisation >= 0.85) return 'success';
  if (utilisation >= 0.75) return 'info';
  if (utilisation >= 0.60) return 'warning';
  return 'error';
};

export const KeyPerformanceIndicators: React.FC<KeyPerformanceIndicatorsProps> = ({
  summary,
  onCardClick,
}) => {
  const utilisationVariant = getUtilisationVariant(summary.averageUtilisation);
  const varianceSign = summary.varianceFromTarget >= 0 ? '+' : '-';
  const variancePct = Math.abs(summary.varianceFromTarget * 100).toFixed(1);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Average Utilisation */}
      <NavigationMetricCardAlt
        title="Average Utilisation"
        value={`${(summary.averageUtilisation * 100).toFixed(1)}%`}
        icon={Target}
        variant={utilisationVariant}
        change={`${varianceSign}${variancePct}% vs target of ${(summary.targetUtilisation * 100).toFixed(0)}%`}
        onClick={onCardClick ? () => onCardClick('utilisation') : undefined}
        ariaLabel="Filter by utilisation"
      />

      {/* Total Hours */}
      <NavigationMetricCardAlt
        title="Total Hours"
        value={summary.totalHours.toFixed(1)}
        icon={Clock}
        variant="info"
        change={`Across ${summary.totalEmployees} ${summary.totalEmployees === 1 ? 'employee' : 'employees'}`}
        onClick={onCardClick ? () => onCardClick('hours') : undefined}
        ariaLabel="Filter by total hours"
      />

      {/* Billable Hours */}
      <NavigationMetricCardAlt
        title="Billable Hours"
        value={summary.totalBillableHours.toFixed(1)}
        icon={PoundSterling}
        variant="success"
        change={`${summary.billablePercentage.toFixed(0)}% billable · ${summary.totalNonBillableHours.toFixed(1)} non-billable`}
        onClick={onCardClick ? () => onCardClick('billable') : undefined}
        ariaLabel="Filter by billable hours"
      />

      {/* Peak Activity */}
      <NavigationMetricCardAlt
        title="Peak Activity"
        value={summary.peakUtilisationDay.charAt(0).toUpperCase() + summary.peakUtilisationDay.slice(1)}
        icon={Activity}
        variant="info"
        change={`Lowest activity: ${summary.lowestUtilisationDay.charAt(0).toUpperCase() + summary.lowestUtilisationDay.slice(1)}`}
        onClick={onCardClick ? () => onCardClick('peak') : undefined}
        ariaLabel="View peak activity"
      />
    </div>
  );
};
